import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

// API Configuration
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API_URL = process.env.REACT_APP_API_URL;

if (!API_BASE || !API_URL) {
  console.error('❌ CRITICAL: API URLs not configured in EventManagement!');
}

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    eventType: 'Webinar',
    startDate: '',
    endDate: '',
    timezone: 'EST',
    duration: '',
    medium: 'Online',
    category: 'Free',
    membersOnly: false,
    price: 0,
    memberPrice: '',
    nonMemberPrice: '',
    earlyBirdPrice: '',
    studentPrice: '',
    location: '',
    meetingLink: '',
    meetingId: '',
    meetingPasscode: '',
    registrationLink: '',
    maxParticipants: 100,
    status: 'Draft',
    image_url: '',
    speakers: '',
    youtubeVideoId: '',
    language: 'English',
    cpdTiers: []
  });
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  // Registration management state
  const [showRegistrations, setShowRegistrations] = useState(false);
  const [regEvent, setRegEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [regStats, setRegStats] = useState(null);
  const [regFilters, setRegFilters] = useState({ paymentStatus: '', cpd: '', search: '' });
  const [regLoading, setRegLoading] = useState(false);

  // Timezone offset map: how many hours to ADD to local time to get UTC
  // EST is UTC-5, so add +5 to go from EST → UTC
  // CST is UTC-6, so add +6 to go from CST → UTC
  // GMT is UTC+0, so add 0
  const TZ_OFFSETS = { EST: -5, CST: -6, GMT: 0 };

  // Convert datetime-local string (as entered in selected TZ) → UTC ISO string
  const localToUTC = (localStr, timezone) => {
    if (!localStr) return localStr;
    const offset = TZ_OFFSETS[timezone] || 0;
    const [datePart, timePart] = localStr.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    // UTC = local - offset  (EST offset is -5, so UTC = local - (-5) = local + 5)
    // e.g. 8:00 AM EST → 8 - (-5) = 13:00 UTC ✓
    const utcDate = new Date(Date.UTC(year, month - 1, day, hours - offset, minutes));
    return utcDate.toISOString().slice(0, 16);
  };

  // Convert UTC string → datetime-local string in a given timezone
  const utcToLocal = (utcStr, timezone) => {
    if (!utcStr) return '';
    const offset = TZ_OFFSETS[timezone] || 0;
    const date = new Date(utcStr);
    const localMs = date.getTime() + offset * 3600000;
    return new Date(localMs).toISOString().slice(0, 16);
  };

  // Return object with EST, CST, GMT formatted display strings from a UTC date string
  const allTimezones = (utcStr) => {
    if (!utcStr) return { EST: 'N/A', CST: 'N/A', GMT: 'N/A' };
    const datePart = new Date(utcStr).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
    const fmtTime = (offset) => {
      const d = new Date(new Date(utcStr).getTime() + offset * 3600000);
      const h = d.getUTCHours(), m = d.getUTCMinutes();
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hh = h % 12 || 12;
      return `${hh}:${String(m).padStart(2, '0')} ${ampm}`;
    };
    return {
      date: datePart,
      EST: fmtTime(-5),
      CST: fmtTime(-6),
      GMT: fmtTime(0)
    };
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/admin/events`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setEvents(data.data.events || []);
      } else {
        toast.error(data.message || 'Failed to fetch events');
      }
    } catch (err) {
      console.error('Error fetching events:', err);
      toast.error('Failed to fetch events');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let uploadedImagePath = formData.image_url;

      // First, upload image if a new one was selected
      if (bannerFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', bannerFile);

        const token = localStorage.getItem('adminToken');
        console.log('📷 Uploading event image...');
        const imageResponse = await fetch(`${API_URL}/admin-upload/events/image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Do NOT set Content-Type - let browser set it with boundary
          },
          body: imageFormData
        });

        const imageData = await imageResponse.json();
        console.log('Image upload response:', imageData);

        if (imageData.success) {
          uploadedImagePath = imageData.data.imagePath;
          console.log('✅ Image uploaded successfully:', uploadedImagePath);
        } else {
          throw new Error(imageData.message || 'Failed to upload image');
        }
      }

      // Now save the event with the image path
      const eventData = {
        ...formData,
        image_url: uploadedImagePath,
        // Convert start/end times from selected timezone to UTC before saving
        startDate: localToUTC(formData.startDate, formData.timezone),
        endDate: localToUTC(formData.endDate, formData.timezone)
      };

      const token = localStorage.getItem('adminToken');
      const url = currentEvent
        ? `${API_URL}/admin/events/${currentEvent.id}`
        : `${API_URL}/admin/events`;

      const method = currentEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });

      const data = await response.json();

      if (data.success) {
        setShowModal(false);
        fetchEvents();
        toast.success(`Event ${currentEvent ? 'updated' : 'created'} successfully`);
        resetForm();
      } else {
        toast.error(data.message || `Failed to ${currentEvent ? 'update' : 'create'} event`);
      }
    } catch (err) {
      console.error('Error saving event:', err);
      toast.error(err.message || `Failed to ${currentEvent ? 'update' : 'create'} event`);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/admin/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        fetchEvents();
        toast.success('Event deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete event');
      }
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event');
    }
  };

  const openCreateModal = () => {
    resetForm();
    setCurrentEvent(null);
    setShowModal(true);
  };

  const openEditModal = (event) => {
    setCurrentEvent(event);
    setFormData({
      name: event.name || '',
      description: event.description || '',
      eventType: event.event_type || 'Webinar',
      startDate: utcToLocal(event.start_date, 'EST'),
      endDate: utcToLocal(event.end_date, 'EST'),
      timezone: 'EST',
      duration: event.duration || '',
      medium: event.medium || 'Online',
      category: event.category || 'Free',
      membersOnly: event.members_only === 1 || event.members_only === true,
      price: event.price || 0,
      memberPrice: event.member_price || '',
      nonMemberPrice: event.non_member_price || '',
      earlyBirdPrice: event.early_bird_price || '',
      studentPrice: event.student_price || '',
      location: event.location || '',
      meetingLink: event.meeting_link || '',
      meetingId: event.meeting_id || '',
      meetingPasscode: event.meeting_passcode || '',
      registrationLink: event.registration_link || '',
      maxParticipants: event.max_participants || 100,
      status: event.status || 'Draft',
      image_url: event.image_url || '',
      speakers: event.speakers || '',
      youtubeVideoId: event.youtube_video_id || '',
      language: event.language || 'English',
      cpdTiers: (() => {
        try {
          const tiers = typeof event.cpd_tiers === 'string' ? JSON.parse(event.cpd_tiers) : event.cpd_tiers;
          return Array.isArray(tiers) ? tiers.map(t => ({ points: t.points, price: t.price, payment_link: t.payment_link || '' })) : [];
        } catch (e) {
          return [];
        }
      })()
    });
    setBannerPreview(event.image_url ? `${API_BASE}${event.image_url}` : null);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      eventType: 'Webinar',
      startDate: '',
      endDate: '',
      timezone: 'EST',
      duration: '',
      medium: 'Online',
      category: 'Free',
      membersOnly: false,
      price: 0,
      memberPrice: '',
      nonMemberPrice: '',
      earlyBirdPrice: '',
      studentPrice: '',
      location: '',
      meetingLink: '',
      meetingId: '',
      meetingPasscode: '',
      registrationLink: '',
      maxParticipants: 100,
      status: 'Draft',
      image_url: '',
      speakers: '',
      youtubeVideoId: '',
      language: 'English',
      cpdTiers: []
    });
    setBannerFile(null);
    setBannerPreview(null);
  };

  // ── CPD tier editor handlers ──
  const addCpdTier = () => {
    setFormData(prev => ({ ...prev, cpdTiers: [...prev.cpdTiers, { points: '', price: '', payment_link: '' }] }));
  };

  const updateCpdTier = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cpdTiers: prev.cpdTiers.map((tier, i) => i === index ? { ...tier, [field]: value } : tier)
    }));
  };

  const removeCpdTier = (index) => {
    setFormData(prev => ({ ...prev, cpdTiers: prev.cpdTiers.filter((_, i) => i !== index) }));
  };

  // ── Registration management ──
  const fetchRegistrations = async (eventId, filters) => {
    try {
      setRegLoading(true);
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (filters.paymentStatus) params.append('paymentStatus', filters.paymentStatus);
      if (filters.cpd) params.append('cpd', filters.cpd);
      if (filters.search) params.append('search', filters.search);

      const response = await fetch(`${API_URL}/admin/events/${eventId}/registrations?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        setRegistrations(data.data.registrations || []);
        setRegStats(data.data.stats || null);
      } else {
        toast.error(data.message || 'Failed to fetch registrations');
      }
    } catch (err) {
      console.error('Error fetching registrations:', err);
      toast.error('Failed to fetch registrations');
    } finally {
      setRegLoading(false);
    }
  };

  const openRegistrations = (event) => {
    const initialFilters = { paymentStatus: '', cpd: '', search: '' };
    setRegEvent(event);
    setRegFilters(initialFilters);
    setRegistrations([]);
    setRegStats(null);
    setShowRegistrations(true);
    fetchRegistrations(event.id, initialFilters);
  };

  const handleRegFilterChange = (field, value) => {
    const next = { ...regFilters, [field]: value };
    setRegFilters(next);
    if (regEvent) fetchRegistrations(regEvent.id, next);
  };

  const exportRegistrations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (regFilters.paymentStatus) params.append('paymentStatus', regFilters.paymentStatus);
      if (regFilters.cpd) params.append('cpd', regFilters.cpd);
      if (regFilters.search) params.append('search', regFilters.search);

      const response = await fetch(`${API_URL}/admin/events/${regEvent.id}/registrations/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registrations_${(regEvent.name || 'event').replace(/\s+/g, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Registrations exported');
    } catch (err) {
      console.error('Export error:', err);
      toast.error('Failed to export registrations');
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image must be less than 5MB');
        return;
      }
      setBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 fw-bold mb-0">Event Management</h2>
        <button className="btn btn-primary" onClick={openCreateModal}>
          Create New Event
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Event Name</th>
                  <th>Type</th>
                  <th>Language</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Medium</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map(event => (
                  <tr key={event.id}>
                    <td>{event.name}</td>
                    <td>{event.event_type}</td>
                    <td>
                      {event.language ? (
                        <span className="badge bg-info text-dark">{event.language}</span>
                      ) : (
                        <span className="text-muted small">English</span>
                      )}
                    </td>
                    <td>
                      {event.start_date ? (() => {
                        const tz = allTimezones(event.start_date);
                        return (
                          <span>
                            <span className="d-block text-muted small">{tz.date}</span>
                            <span className="badge bg-secondary me-1">EST {tz.EST}</span>
                            <span className="badge bg-info text-dark me-1">CST {tz.CST}</span>
                            <span className="badge bg-dark">GMT {tz.GMT}</span>
                          </span>
                        );
                      })() : 'N/A'}
                    </td>
                    <td>
                      {event.end_date ? (() => {
                        const tz = allTimezones(event.end_date);
                        return (
                          <span>
                            <span className="d-block text-muted small">{tz.date}</span>
                            <span className="badge bg-secondary me-1">EST {tz.EST}</span>
                            <span className="badge bg-info text-dark me-1">CST {tz.CST}</span>
                            <span className="badge bg-dark">GMT {tz.GMT}</span>
                          </span>
                        );
                      })() : 'N/A'}
                    </td>
                    <td>{event.medium}</td>
                    <td>
                      <span className={`badge ${event.status === 'Published' ? 'bg-success' : 'bg-warning'}`}>
                        {event.status}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-primary me-2" onClick={() => openEditModal(event)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-success me-2" onClick={() => openRegistrations(event)}>
                        Registrations
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(event.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {events.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">No events found</p>
              <button className="btn btn-primary" onClick={openCreateModal}>
                Create Your First Event
              </button>
            </div>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{currentEvent ? 'Edit Event' : 'Create New Event'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Event Name *</label>
                      <input type="text" className="form-control" name="name" value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Event Type</label>
                      <select className="form-select" name="eventType" value={formData.eventType} onChange={handleInputChange}>
                        <option value="Webinar">Webinar</option>
                        <option value="Workshop">Workshop</option>
                        <option value="Training">Training</option>
                        <option value="Conference">Conference</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows="3"></textarea>
                  </div>

                  {/* Show Speaker(s) field for Webinar and Conference */}
                  {(formData.eventType === 'Webinar' || formData.eventType === 'Conference') && (
                    <div className="mb-3">
                      <label className="form-label">Speaker(s) Details</label>
                      <textarea
                        className="form-control"
                        name="speakers"
                        value={formData.speakers}
                        onChange={handleInputChange}
                        rows="3"
                        placeholder="Enter speaker names and credentials. Example: Dr. John Doe, MD - Pulmonologist; Dr. Jane Smith, PhD - Respiratory Therapist"
                      ></textarea>
                      <small className="form-text text-muted">Enter speaker names, titles, and credentials separated by semicolons</small>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="form-label">Event Banner/Image</label>
                    <input type="file" className="form-control" accept="image/*" onChange={handleBannerChange} />
                    {bannerPreview && (
                      <div className="mt-2">
                        <img src={bannerPreview} alt="Banner preview" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }} className="rounded" />
                      </div>
                    )}
                    <small className="form-text text-muted">Maximum 5MB. Supported: JPG, PNG, GIF, WebP</small>
                  </div>

                  <div className="row">
                    <div className="col-md-5 mb-3">
                      <label className="form-label">Start Date & Time *</label>
                      <input type="datetime-local" className="form-control" name="startDate" value={formData.startDate} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-5 mb-3">
                      <label className="form-label">End Date & Time *</label>
                      <input type="datetime-local" className="form-control" name="endDate" value={formData.endDate} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label">Timezone</label>
                      <select className="form-select" name="timezone" value={formData.timezone} onChange={handleInputChange}>
                        <option value="EST">EST (UTC-5)</option>
                        <option value="CST">CST (UTC-6)</option>
                        <option value="GMT">GMT (UTC+0)</option>
                      </select>
                    </div>
                  </div>
                  {/* Live timezone preview */}
                  {formData.startDate && (
                    <div className="alert alert-info py-2 mb-3">
                      <small className="fw-bold">Start time in all zones: </small>
                      {(() => {
                        const utc = localToUTC(formData.startDate, formData.timezone);
                        const tz = allTimezones(utc + ':00');
                        return <small><strong>EST</strong> {tz.EST} &nbsp;|&nbsp; <strong>CST</strong> {tz.CST} &nbsp;|&nbsp; <strong>GMT</strong> {tz.GMT}</small>;
                      })()}
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Medium</label>
                      <select className="form-select" name="medium" value={formData.medium} onChange={handleInputChange}>
                        <option value="Online">Online</option>
                        <option value="Onsite">Onsite</option>
                      </select>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Category</label>
                      <select className="form-select" name="category" value={formData.category} onChange={handleInputChange}>
                        <option value="Free">Free</option>
                        <option value="Paid">Paid</option>
                      </select>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Language</label>
                      <select className="form-select" name="language" value={formData.language} onChange={handleInputChange}>
                        <option value="English">English</option>
                        <option value="French">French</option>
                        <option value="Portuguese">Portuguese</option>
                        <option value="Spanish">Spanish</option>
                        <option value="Arabic">Arabic</option>
                      </select>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Event Access *</label>
                      <select
                        className="form-select"
                        name="membersOnly"
                        value={formData.membersOnly ? 'members' : 'all'}
                        onChange={(e) => setFormData(prev => ({ ...prev, membersOnly: e.target.value === 'members' }))}
                      >
                        <option value="all">Open to All</option>
                        <option value="members">Members Only</option>
                      </select>
                      <small className="form-text text-muted">
                        {formData.membersOnly ? 'Only logged-in ARCA members can register' : 'Anyone can register, no login needed'}
                      </small>
                    </div>
                  </div>

                  {/* Show pricing fields if Paid is selected */}
                  {formData.category === 'Paid' && (
                    <div className="mb-3">
                      <h6 className="fw-bold mb-3" style={{ color: '#6B8E23' }}>Event Pricing (USD)</h6>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Member Price</label>
                          <input
                            type="number"
                            className="form-control"
                            name="memberPrice"
                            value={formData.memberPrice}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            placeholder="Enter member price"
                          />
                          <small className="text-muted">Price for ARCA members</small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Non-Member Price</label>
                          <input
                            type="number"
                            className="form-control"
                            name="nonMemberPrice"
                            value={formData.nonMemberPrice}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            placeholder="Enter non-member price"
                          />
                          <small className="text-muted">Price for non-members</small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Early Bird Price</label>
                          <input
                            type="number"
                            className="form-control"
                            name="earlyBirdPrice"
                            value={formData.earlyBirdPrice}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            placeholder="Enter early bird price"
                          />
                          <small className="text-muted">Discounted early registration price</small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Student Price</label>
                          <input
                            type="number"
                            className="form-control"
                            name="studentPrice"
                            value={formData.studentPrice}
                            onChange={handleInputChange}
                            min="0"
                            step="0.01"
                            placeholder="Enter student price"
                          />
                          <small className="text-muted">Discounted student price</small>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* CPD Points Pricing — applies to both Free and Paid events */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>CPD Points Pricing (USD)</h6>
                      <button type="button" className="btn btn-sm btn-outline-success" onClick={addCpdTier}>
                        + Add CPD Option
                      </button>
                    </div>
                    <small className="form-text text-muted d-block mb-2">
                      Optional. Add CPD point options with their prices (e.g. 5 points, 10 points). Attendees who require CPD points must pay the selected option's price — even for free events. Payment link is optional and can be added later.
                    </small>
                    {formData.cpdTiers.length === 0 && (
                      <div className="text-muted small fst-italic">No CPD options configured. Attendees will not be able to request CPD points for this event.</div>
                    )}
                    {formData.cpdTiers.map((tier, index) => (
                      <div className="row g-2 align-items-end mb-2" key={index}>
                        <div className="col-md-3">
                          <label className="form-label small mb-1">CPD Points</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={tier.points}
                            onChange={(e) => updateCpdTier(index, 'points', e.target.value)}
                            min="1"
                            placeholder="e.g. 5"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small mb-1">Price (USD)</label>
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={tier.price}
                            onChange={(e) => updateCpdTier(index, 'price', e.target.value)}
                            min="0"
                            step="0.01"
                            placeholder="e.g. 25.00"
                          />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label small mb-1">Payment Link (optional)</label>
                          <input
                            type="url"
                            className="form-control form-control-sm"
                            value={tier.payment_link || ''}
                            onChange={(e) => updateCpdTier(index, 'payment_link', e.target.value)}
                            placeholder="https://paystack.shop/pay/..."
                          />
                        </div>
                        <div className="col-md-1">
                          <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => removeCpdTier(index)} title="Remove">
                            &times;
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {formData.medium === 'Onsite' ? (
                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input type="text" className="form-control" name="location" value={formData.location} onChange={handleInputChange} />
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Meeting Link</label>
                        <input type="url" className="form-control" name="meetingLink" value={formData.meetingLink} onChange={handleInputChange} />
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Meeting ID</label>
                          <input type="text" className="form-control" name="meetingId" value={formData.meetingId} onChange={handleInputChange} placeholder="816 1841 7148" />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Meeting Passcode</label>
                          <input type="text" className="form-control" name="meetingPasscode" value={formData.meetingPasscode} onChange={handleInputChange} placeholder="944593" />
                        </div>
                      </div>
                    </>
                  )}

                  {/* External Registration Link — only for Free Online events */}
                  {formData.medium === 'Online' && formData.category === 'Free' && (
                    <div className="mb-3">
                      <label className="form-label">External Registration Link</label>
                      <input
                        type="url"
                        className="form-control"
                        name="registrationLink"
                        value={formData.registrationLink}
                        onChange={handleInputChange}
                        placeholder="https://docs.google.com/forms/..."
                      />
                      <small className="form-text text-muted">
                        Paste your Google Form or other registration URL here. A "Register Now" button will appear on the event page.
                      </small>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Max Participants</label>
                      <input type="number" className="form-control" name="maxParticipants" value={formData.maxParticipants} onChange={handleInputChange} min="1" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select className="form-select" name="status" value={formData.status} onChange={handleInputChange}>
                        <option value="Draft">Draft</option>
                        <option value="Published">Published</option>
                      </select>
                    </div>
                  </div>

                  {/* YouTube Video ID for Past Event Recordings */}
                  <div className="mb-3">
                    <label className="form-label">YouTube Video ID (for Past Event Recording)</label>
                    <input
                      type="text"
                      className="form-control"
                      name="youtubeVideoId"
                      value={formData.youtubeVideoId}
                      onChange={handleInputChange}
                      placeholder="e.g., dQw4w9WgXcQ"
                      maxLength="50"
                    />
                    <small className="form-text text-muted">
                      Enter the 11-character YouTube video ID from the URL. For example, from https://youtube.com/watch?v=<strong>dQw4w9WgXcQ</strong>, enter: <code>dQw4w9WgXcQ</code>
                    </small>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{currentEvent ? 'Update Event' : 'Create Event'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Registrations Management Modal */}
      {showRegistrations && regEvent && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Registrations — {regEvent.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowRegistrations(false)}></button>
              </div>
              <div className="modal-body">
                {/* Stats */}
                {regStats && (
                  <div className="row g-2 mb-3">
                    <div className="col">
                      <div className="border rounded p-2 text-center">
                        <div className="fw-bold h5 mb-0">{regStats.total}</div>
                        <small className="text-muted">Total</small>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-2 text-center">
                        <div className="fw-bold h5 mb-0 text-success">{regStats.paid}</div>
                        <small className="text-muted">Paid</small>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-2 text-center">
                        <div className="fw-bold h5 mb-0 text-warning">{regStats.pending}</div>
                        <small className="text-muted">Pending</small>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-2 text-center">
                        <div className="fw-bold h5 mb-0" style={{ color: '#6B8E23' }}>{regStats.cpd}</div>
                        <small className="text-muted">CPD Requests</small>
                      </div>
                    </div>
                    <div className="col">
                      <div className="border rounded p-2 text-center">
                        <div className="fw-bold h5 mb-0" style={{ color: '#6B8E23' }}>${Number(regStats.revenue).toFixed(2)}</div>
                        <small className="text-muted">Revenue</small>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filters + Export */}
                <div className="row g-2 mb-3">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Search name or email..."
                      value={regFilters.search}
                      onChange={(e) => handleRegFilterChange('search', e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={regFilters.paymentStatus}
                      onChange={(e) => handleRegFilterChange('paymentStatus', e.target.value)}
                    >
                      <option value="">All Payment Statuses</option>
                      <option value="Paid">Paid</option>
                      <option value="Pending">Pending</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select form-select-sm"
                      value={regFilters.cpd}
                      onChange={(e) => handleRegFilterChange('cpd', e.target.value)}
                    >
                      <option value="">All Attendees</option>
                      <option value="yes">CPD Only</option>
                      <option value="no">Non-CPD Only</option>
                    </select>
                  </div>
                  <div className="col-md-2 text-end">
                    <button className="btn btn-sm btn-success w-100" onClick={exportRegistrations}>
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* Table */}
                {regLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : registrations.length === 0 ? (
                  <div className="text-center py-5 text-muted">No registrations found</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Country</th>
                          <th>Organization</th>
                          <th>CPD</th>
                          <th>Payment</th>
                          <th>Amount</th>
                          <th>Registered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.map(reg => (
                          <tr key={reg.id}>
                            <td>{reg.first_name} {reg.last_name}</td>
                            <td>{reg.email}</td>
                            <td>{reg.phone || '—'}</td>
                            <td>{reg.country || '—'}</td>
                            <td>{reg.organization || '—'}</td>
                            <td>
                              {reg.requires_cpd ? (
                                <span className="badge" style={{ backgroundColor: '#6B8E23' }} title={`${reg.governing_body || ''} | PIN: ${reg.pin_number || ''} | ${reg.area_of_study || ''} | ${reg.profession || ''}`}>
                                  {reg.cpd_points ? `${reg.cpd_points} pts` : 'Yes'}
                                </span>
                              ) : (
                                <span className="text-muted small">No</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge ${reg.payment_status === 'Paid' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {reg.payment_status}
                              </span>
                            </td>
                            <td>{reg.payment_amount ? `$${Number(reg.payment_amount).toFixed(2)}` : '—'}</td>
                            <td className="small text-muted">{reg.created_at ? new Date(reg.created_at).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* CPD details for CPD registrants */}
                {!regLoading && registrations.some(r => r.requires_cpd) && (
                  <>
                    <h6 className="fw-bold mt-4 mb-2" style={{ color: '#6B8E23' }}>CPD Details</h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-bordered align-middle">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Governing Body</th>
                            <th>PIN Number</th>
                            <th>Area of Study</th>
                            <th>Profession</th>
                            <th>Points</th>
                            <th>CPD Fee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrations.filter(r => r.requires_cpd).map(reg => (
                            <tr key={`cpd-${reg.id}`}>
                              <td>{reg.first_name} {reg.last_name}</td>
                              <td>{reg.governing_body || '—'}</td>
                              <td>{reg.pin_number || '—'}</td>
                              <td>{reg.area_of_study || '—'}</td>
                              <td>{reg.profession || '—'}</td>
                              <td>{reg.cpd_points || '—'}</td>
                              <td>{reg.cpd_fee ? `$${Number(reg.cpd_fee).toFixed(2)}` : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRegistrations(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventManagement;
